interface PriorityType {
    priority: number;
}

export default class FilterService {
    /**
     * This relies on parent options appearing before child ones.
     * First pass to find matching options with priority. Keep any parent options.
     * Second pass to rewrite priorities and remove parent options that have no matching children.
     * @param options - options to filter on
     * @param filter - filter string to match on
     * @param labelKey - key to get label from option
     * @param anchorOnClickKey - key to check whether option is clickable
     * @param isChild - function to check whether option is parent or child
     */
    public static filterOptions<T, K extends keyof T>(
        options: T[],
        filter: string,
        labelKey: K,
        isChild: (e: T) => boolean,
        anchorOnClickKey?: K
    ): Array<T | (T & PriorityType)> {
        if (!filter) return options;

        // Track the best priority of group options in array
        const familyPriorities: number[] = [];
        const firstPass: any[] = options.reduce((acc, e) => {
            // Add clickable link
            if (anchorOnClickKey && e[anchorOnClickKey]) {
                acc.push(e);
                return acc;
            }
            // Get option with matching priority
            const matchedOption = this.optionMatchWithPriority<T, K>(e, filter, labelKey);

            // If we are at a parent option
            if (!isChild(e)) {
                // Add it to the family priorities
                familyPriorities.push(matchedOption.priority);
                // Keep in array for now
                acc.push(matchedOption);
            }
            // If we have matching child option
            else if (matchedOption.priority !== -1) {
                // If we have a non-matching parent option or better priority than rest of family (i.e. the
                // child has better match than family)
                if (
                    familyPriorities[familyPriorities.length - 1] === -1 ||
                    matchedOption.priority < familyPriorities[familyPriorities.length - 1]
                ) {
                    // Replace family priority with child priority
                    familyPriorities[familyPriorities.length - 1] = matchedOption.priority;
                }
                // Add matching child to array
                acc.push(matchedOption);
            }
            return acc;
        }, []);

        let parentPointer = -1;
        return firstPass.reduce((acc, e) => {
            if (!isChild(e)) {
                // First item will be parent one
                parentPointer++;
            }
            // Set option priority to the best priority of family
            e.priority = familyPriorities[parentPointer];
            // If that priority is not -1 then keep in array
            if (e.priority !== -1) acc.push(e);

            return acc;
        }, []);
    }

    /**
     * This matches the option to filter by first checking if the label starts with the filter. If not then it splits up
     * the label into individual words and finds the index of the first word that starts with the filter.
     * It returns the option with an added priority property. A non-match has priority -1. A match has priority >= 0
     * with the best priority being 0
     * @param option - option to check
     * @param filter - filter string to match on
     * @param labelKey - key to get label from option
     */
    private static optionMatchWithPriority<T, K extends keyof T>(
        option: any,
        filter: string,
        labelKey: K
    ): T & PriorityType {
        if (typeof option[labelKey] !== 'string')
            throw new Error(`Object does not have a string property at key ${labelKey.toString()}`);
        const optionLabel = option[labelKey].toLowerCase();
        // If it matches exactly then priority is 0
        if (optionLabel.startsWith(filter))
            return {
                ...option,
                priority: 0
            };
        // Otherwise split up into words and find index of first match
        const optionWords = optionLabel.split(/,\s&\s|\s&\s|,\s|\s/);
        const index = optionWords.findIndex((word: string) => word.startsWith(filter));
        return {
            ...option,
            priority: index
        };
    }
}
